import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plug, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { PageDescription, PanelTitle } from '@reflex/ui/components/layout/Typography';
import { MetaListItem } from '@reflex/ui/components/shared/MetaListItem';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { PluginIcon } from '@reflex/ui/components/shared/PluginIcon';
import { CanGuard } from '@reflex/ui/client/permissions';
import { PluginInstallationStatusPill } from '@/components/plugins/PluginInstallationStatusPill';
import { PluginOrgSetupPanel } from '@/components/plugins/PluginOrgSetupPanel';
import { invalidateOrgPluginInstallationQueries } from '@/hooks/plugins/invalidateOrgPluginInstallationQueries';
import { useOrgPluginUninstallPreview } from '@/hooks/plugins/useOrgPluginUninstallPreview';
import { usePlugins } from '@/hooks/config/usePlugins';
import { api } from '@/lib/api';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import type { InstallationView } from '@/lib/types';
import { PluginAboutSection } from './PluginAboutSection';
import { PluginContributionsSection } from './PluginContributionsSection';
import { PluginPreviewsSection } from './PluginPreviewsSection';

export interface PluginDetailContentProps {
  /** The plugin installation row (typically from `useOrgPlugin`). */
  row: InstallationView;
  /** Active organization id — required for install/uninstall mutations. */
  orgId: string | null;
  /**
   * Cached list of all per-org plugin rows (typically from
   * `useOrgPlugins`). Used to compute system-bucket membership and
   * dependency status badges. When absent, status badges fall back to
   * generic placeholders.
   */
  allRows:
    | { installed: InstallationView[]; available: InstallationView[]; system: InstallationView[] }
    | undefined;
  /**
   * Optional callback fired when the user clicks a dependency or
   * "Required by" entry. When provided, dependency entries render as
   * `<button>`s that call this handler instead of `<Link>`s — useful
   * when the content lives inside a sheet/dialog where switching to
   * the dependency's plugin should update the same surface rather
   * than navigating away.
   */
  onSelectPlugin?: (pluginName: string) => void;
  /**
   * Optional callback fired after a successful uninstall completes.
   * When provided, replaces the default `navigate(orgRoutes.orgPlugins)`
   * behavior — useful when the content lives inside a sheet/dialog
   * where the surrounding context handles dismissal.
   */
  onAfterUninstall?: () => void;
}

/**
 * Renders the rich body of an org-scoped plugin detail surface: header
 * (icon, name, version, status, action button), error alert, tags,
 * About / Contributions / Previews sections, and dependency lists.
 *
 * Reused by both the standalone `/orgs/${slug}/plugins/:name` page and the
 * org-setup wizard's slide-over sheet. The two optional callback props
 * (`onSelectPlugin`, `onAfterUninstall`) let the sheet override the
 * default react-router navigation hardcoded into the dependency links
 * and uninstall flow.
 */
export function PluginDetailContent({
  row,
  orgId,
  allRows,
  onSelectPlugin,
  onAfterUninstall,
}: PluginDetailContentProps) {
  const orgRoutes = useOrgRoutes();
  const { manifests } = usePlugins();
  // The InstallationView shape doesn't expose `installable` directly, so
  // membership in the cached system bucket is how we tell a system plugin
  // (where install/uninstall buttons should not render) from an
  // installable one.
  const inSystemBucket = allRows?.system.some((p) => p.pluginName === row.pluginName) ?? false;

  // Compute lookup of ALL installed names (installed + system) for
  // dependency-status badges and install-button gating.
  const installedNames = allRows
    ? new Set([
        ...allRows.installed.map((p) => p.pluginName),
        ...allRows.system.map((p) => p.pluginName),
      ])
    : null;

  // Names of all plugins for resolving "exists" badges.
  const knownNames = allRows
    ? new Set([
        ...allRows.installed.map((p) => p.pluginName),
        ...allRows.available.map((p) => p.pluginName),
        ...allRows.system.map((p) => p.pluginName),
      ])
    : null;

  const isInstalled = row.status === 'installed';
  const isTransient = row.status === 'installing' || row.status === 'uninstalling';
  const missingDeps = row.dependencies.filter((dep) => installedNames && !installedNames.has(dep));

  // Match the per-org installation row against the broader plugin
  // manifest catalog so the new About / Contributions / Preview sections
  // can read everything the plugin declares (overview cards, web
  // contributions, marketing previews) — not just the installation row.
  const manifest = useMemo(
    () => manifests.find((m) => m.name === row.pluginName),
    [manifests, row.pluginName],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* A div, not <header>: outside <main> this computes a banner
          landmark, and a nested banner fails axe
          `landmark-banner-is-top-level`. */}
      <div className="flex flex-wrap items-start gap-4" data-testid="plugin-detail-header">
        <PluginIcon icon={row.icon} size="lg" className="shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <PanelTitle>{row.pluginName}</PanelTitle>
            <Badge variant="secondary" className="shrink-0">
              v{row.version}
            </Badge>
            <PluginInstallationStatusPill status={row.status} system={inSystemBucket} />
          </div>
          {row.description && <PageDescription className="mt-0">{row.description}</PageDescription>}
          {row.tags && row.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {row.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-2xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {!inSystemBucket && orgId && (
          <CanGuard permission="plugins:install">
            <DetailActions
              row={row}
              orgId={orgId}
              isInstalled={isInstalled}
              isTransient={isTransient}
              missingDeps={missingDeps}
              onAfterUninstall={onAfterUninstall}
            />
          </CanGuard>
        )}
      </div>

      {row.lastError && (
        <Alert variant="destructive" data-testid="plugin-detail-last-error">
          <AlertTitle>Last action failed</AlertTitle>
          <AlertDescription>{row.lastError}</AlertDescription>
        </Alert>
      )}

      {manifest?.web?.orgSetupComponent && (
        <CanGuard permission="integrations:read">
          <SectionCard
            data-testid="plugin-detail-setup"
            title="Connection"
            icon={<Plug />}
            description={manifest.web.orgSetupComponent.label}
            className="gap-2 py-4 sm:gap-3 sm:py-4"
          >
            <PluginOrgSetupPanel plugin={manifest} hideLabel />
          </SectionCard>
        </CanGuard>
      )}

      <PluginPreviewsSection manifest={manifest} />

      <PluginAboutSection manifest={manifest} />

      <PluginContributionsSection manifest={manifest} />

      <RequiredCapabilitiesList row={row} />

      <DependencyList
        title="Requires"
        names={row.dependencies}
        installedNames={installedNames}
        knownNames={knownNames}
        plugins={allRows}
        orgRoutes={orgRoutes}
        testId="plugin-detail-requires"
        onSelectPlugin={onSelectPlugin}
      />

      <DependencyList
        title="Required by"
        names={row.dependents}
        installedNames={installedNames}
        knownNames={knownNames}
        plugins={allRows}
        orgRoutes={orgRoutes}
        testId="plugin-detail-required-by"
        onSelectPlugin={onSelectPlugin}
      />
    </div>
  );
}

interface RequiredCapabilitiesListProps {
  row: InstallationView;
}

/**
 * Renders the plugin's `requiredCapabilities` list with a granted /
 * missing status badge per slug. We derive "missing" from
 * `row.missingCapabilities` (server-computed against the active org's
 * capability set); everything else in `requiredCapabilities` is treated
 * as granted. Hidden entirely when the plugin has no capability gate so
 * the section doesn't add noise to unrestricted plugins.
 */
function RequiredCapabilitiesList({ row }: RequiredCapabilitiesListProps) {
  const required = row.requiredCapabilities ?? [];
  if (required.length === 0) return null;
  const missing = new Set(row.missingCapabilities ?? []);
  return (
    <SectionCard
      data-testid="plugin-detail-required-capabilities"
      title="Requires capabilities"
      contentClassName="space-y-3"
    >
      <ul className="flex flex-col gap-1.5">
        {required.map((cap) => {
          const isMissing = missing.has(cap);
          return (
            <MetaListItem
              key={cap}
              data-testid={`plugin-detail-required-capabilities-${cap}`}
              data-status={isMissing ? 'missing' : 'granted'}
              trailing={
                isMissing ? (
                  <Badge variant="destructive">Not granted</Badge>
                ) : (
                  <Badge variant="default">Granted</Badge>
                )
              }
            >
              <span className="min-w-0 flex-1 truncate font-mono text-sm">{cap}</span>
            </MetaListItem>
          );
        })}
      </ul>
      {missing.size > 0 && (
        <p className="text-xs text-muted-foreground/80">
          A platform admin can grant the missing capability from the organization admin page.
        </p>
      )}
    </SectionCard>
  );
}

interface DependencyListProps {
  title: string;
  names: string[];
  installedNames: Set<string> | null;
  knownNames: Set<string> | null;
  plugins:
    | { installed: InstallationView[]; available: InstallationView[]; system: InstallationView[] }
    | undefined;
  orgRoutes: ReturnType<typeof useOrgRoutes>;
  testId: string;
  onSelectPlugin?: (pluginName: string) => void;
}

function DependencyList({
  title,
  names,
  installedNames,
  knownNames,
  plugins,
  orgRoutes,
  testId,
  onSelectPlugin,
}: DependencyListProps) {
  // Hide the entire section when there is nothing to list — empty
  // "No dependencies." / "No other installed plugins depend on this
  // one." placeholders add noise to the detail page for the common
  // case of leaf plugins.
  if (names.length === 0) return null;
  const allRows = plugins
    ? new Map(
        [...plugins.installed, ...plugins.available, ...plugins.system].map(
          (p) => [p.pluginName, p] as const,
        ),
      )
    : null;
  return (
    <SectionCard data-testid={testId} title={title}>
      <ul className="flex flex-col gap-1.5">
        {names.map((dep) => {
          const known = knownNames ? knownNames.has(dep) : true;
          const installed = installedNames ? installedNames.has(dep) : false;
          const row = allRows?.get(dep);
          return (
            <MetaListItem
              key={dep}
              data-testid={`${testId}-${dep}`}
              data-status={installed ? 'installed' : 'missing'}
              trailing={
                row ? (
                  <PluginInstallationStatusPill status={row.status} />
                ) : installed ? (
                  <Badge variant="default">Installed</Badge>
                ) : (
                  <Badge variant="outline">Not installed</Badge>
                )
              }
            >
              {known ? (
                onSelectPlugin ? (
                  <button
                    type="button"
                    onClick={() => onSelectPlugin(dep)}
                    aria-label={`View ${dep} plugin`}
                    className="min-w-0 flex-1 truncate text-left text-sm hover:underline"
                  >
                    {dep}
                  </button>
                ) : (
                  <Link
                    to={orgRoutes.plugin(dep)}
                    aria-label={`View ${dep} plugin`}
                    className="min-w-0 flex-1 truncate text-sm hover:underline"
                  >
                    {dep}
                  </Link>
                )
              ) : (
                <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{dep}</span>
              )}
            </MetaListItem>
          );
        })}
      </ul>
    </SectionCard>
  );
}

interface DetailActionsProps {
  row: InstallationView;
  orgId: string;
  isInstalled: boolean;
  isTransient: boolean;
  missingDeps: string[];
  onAfterUninstall?: () => void;
}

function DetailActions({
  row,
  orgId,
  isInstalled,
  isTransient,
  missingDeps,
  onAfterUninstall,
}: DetailActionsProps) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const installMutation = useMutation({
    mutationFn: () => api.installOrgPlugin(orgId, row.pluginName),
    onSuccess: () => {
      toast.success(`Installed ${row.pluginName}`);
      invalidateOrgPluginInstallationQueries(queryClient, orgId, [row.pluginName]);
    },
    onError: (err) => {
      toast.error('Install failed', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  if (isTransient) {
    return (
      <Button
        type="button"
        variant="outline"
        disabled
        data-testid="plugin-detail-action"
        aria-label={
          row.status === 'installing'
            ? `Installing ${row.pluginName}`
            : `Uninstalling ${row.pluginName}`
        }
        className="gap-1"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        {row.status === 'installing' ? 'Installing…' : 'Uninstalling…'}
      </Button>
    );
  }

  if (isInstalled) {
    return (
      <>
        <Button
          type="button"
          variant="outline"
          onClick={() => setConfirmOpen(true)}
          data-testid="plugin-detail-action"
          aria-label={`Uninstall ${row.pluginName}`}
        >
          Uninstall
        </Button>
        <UninstallConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          row={row}
          orgId={orgId}
          onAfterUninstall={onAfterUninstall}
        />
      </>
    );
  }

  // Available — install action. Disable when deps missing.
  if (missingDeps.length > 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button
              type="button"
              variant="default"
              disabled
              data-testid="plugin-detail-action"
              aria-label={`Install ${row.pluginName} (requires ${missingDeps.join(', ')})`}
            >
              Install
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>Requires: {missingDeps.join(', ')}</TooltipContent>
      </Tooltip>
    );
  }

  if (row.lastError) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => installMutation.mutate()}
        disabled={installMutation.isPending}
        data-testid="plugin-detail-action"
        aria-label={`Retry installing ${row.pluginName}`}
        className="gap-1"
      >
        <RefreshCw className="h-3 w-3" />
        Retry install
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="default"
      onClick={() => installMutation.mutate()}
      disabled={installMutation.isPending}
      data-testid="plugin-detail-action"
      aria-label={`Install ${row.pluginName}`}
    >
      {installMutation.isPending ? 'Installing…' : 'Install'}
    </Button>
  );
}

interface UninstallConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: InstallationView;
  orgId: string;
  onAfterUninstall?: () => void;
}

function UninstallConfirmDialog({
  open,
  onOpenChange,
  row,
  orgId,
  onAfterUninstall,
}: UninstallConfirmDialogProps) {
  const navigate = useNavigate();
  const orgRoutes = useOrgRoutes();
  const queryClient = useQueryClient();
  const previewQuery = useOrgPluginUninstallPreview(orgId, row.pluginName, {
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () => api.uninstallOrgPlugin(orgId, row.pluginName),
    onSuccess: () => {
      toast.success(`Uninstalled ${row.pluginName}`);
      invalidateOrgPluginInstallationQueries(queryClient, orgId, [row.pluginName]);
      // When the consumer wants to handle post-uninstall (e.g. close
      // a parent sheet), skip the default route navigation so the
      // user doesn't get bounced away from the surface they're on.
      if (onAfterUninstall) {
        onAfterUninstall();
      } else {
        navigate(orgRoutes.orgPlugins);
      }
    },
    onError: (err) => {
      toast.error('Uninstall failed', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  // After a successful uninstall request, keep the dialog open with the
  // spinner until the WS event reports a terminal status so the user
  // never sees a stale "uninstalling…" row underneath.
  const isTerminal = row.status === 'uninstalled' || row.status === 'installed';
  const inFlight = mutation.isPending || (mutation.isSuccess && !isTerminal);

  // Close the dialog automatically once the row reaches a terminal
  // state after the user confirmed (so we don't get stuck on the modal
  // while the cascade WS events fire). `onOpenChange` is a parent
  // callback — no local setState happens inside the effect.
  useEffect(() => {
    if (mutation.isSuccess && isTerminal) {
      onOpenChange(false);
    }
  }, [mutation.isSuccess, isTerminal, onOpenChange]);

  function handleConfirm() {
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="plugin-uninstall-confirm">
        <DialogHeader>
          <DialogTitle>Uninstall {row.pluginName}?</DialogTitle>
          <DialogDescription>
            This removes the plugin's UI and routes from this organization. Plugin data is preserved
            and can be re-installed later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {previewQuery.isLoading && (
            <p className="text-xs text-muted-foreground">Computing cascade preview…</p>
          )}
          {previewQuery.data && previewQuery.data.cascaded.length > 0 && (
            <Alert>
              <AlertTitle>This will also uninstall</AlertTitle>
              <AlertDescription>
                <ul className="mt-1 list-disc pl-5 text-sm">
                  {previewQuery.data.cascaded.map((name) => (
                    <li key={name} data-testid={`uninstall-cascade-${name}`}>
                      {name}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={inFlight || mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={inFlight || mutation.isPending}
            data-testid="plugin-uninstall-confirm-button"
            aria-label={`Confirm uninstall of ${row.pluginName}`}
            className="gap-1"
          >
            {(inFlight || mutation.isPending) && <Loader2 className="h-3 w-3 animate-spin" />}
            {inFlight || mutation.isPending ? 'Uninstalling…' : 'Uninstall'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
